import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import { theme } from "../constants/theme";
import { GeneralModal, SortButtonAndMenu } from "../components-molecules";
import { ClusteredList } from "../components-organisms";
import { HeaderWithButtons } from "../components-molecules";
import {
  getVerticalsSymbols,
  addNewVertical,
  getVerticalsSymbolsWithParams,
} from "../redux/actions/getVerticals";

// import { setError } from "../redux/actions/error";

const Verticals = (props: any) => {
  const classes = useStyles();
  const [sortType, setSortType] = React.useState("Asc");
  const [newVerticalName, setNewVerticalName] = React.useState("");
  const [nameError, setNameError] = React.useState(false);
  const [openSearch, setOpenSearch] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [newVerticalDescription, setNewVerticalDescription] =
    React.useState("");
  const dispatch = useDispatch();
  const [openAddShopModal, setAddOpenShopModal] =
    React.useState<boolean>(false);
  const verticalData = useSelector(
    (state: any) => state.VerticalSymbolsReducer.data
  );

  useEffect(() => {
    dispatch(getVerticalsSymbols());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(
      getVerticalsSymbolsWithParams(
        searchTerm ? searchTerm : "",
        sortType.trim() === "" ? "Asc" : sortType
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortType]);

  const handleSubmit = () => {
    if (newVerticalName.trim() === "") {
      setNameError(true);
      return;
    }

    const body = new FormData();
    body.append(
      "json",
      JSON.stringify({
        Name: newVerticalName,
        Description: newVerticalDescription,
      })
    );
    dispatch(
      addNewVertical(body, null, () => {
        setNewVerticalName("");
        setNewVerticalDescription("");
        setAddOpenShopModal(false);
      })
    );
  };

  const bottomTextCreator = React.useCallback(
    (item: any) =>
      `${item?.CategoryCount ?? 0} ${
        item?.CategoryCount === 1 ? "Category" : "Categories"
      } · ${item?.SubCategoryCount ?? 0} ${
        item?.SubCategoryCount === 1 ? "Sub Category" : "Sub Categories"
      } · ${item?.ProductCount ?? 0} ${
        item?.ProductCount === 1 ? "Product" : "Products"
      }`,
    []
  );

  const topTextCreator = React.useCallback(
    (item: any) => `${item?.Name ?? "Unnamed Vertical"}`,
    []
  );

  const pathCreator = React.useCallback(
    (item: any) => `/admin/vertical/${item?.Id}`,
    []
  );

  function debounce(func, timeout = 300) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  }

  const debounceFunction = (st: string, order: string) =>
    debounce(() => {
      dispatch(getVerticalsSymbolsWithParams(st ? st : "", order));
    }, 300);

  const performSearch = React.useCallback(
    debounceFunction,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    performSearch(event.target.value, sortType);
  };

  const updateList = ({ search, order }) => {
    dispatch(getVerticalsSymbolsWithParams(search ? search : "", order));
  };

  return (
    <Box className={classes.container}>
      <CssBaseline />
      <Container className={classes.root}>
        <CssBaseline />
        <HeaderWithButtons title="Verticals">
          <HeaderWithButtons.ButtonArea>
            <div className={classes.mgLeftAuto}>
              <SortButtonAndMenu
                menuItems={[
                  {
                    title: "Alphabetical ascending",
                    value: "Asc",
                    handleSelect: () => {
                      setSortType("Asc");
                      updateList({
                        search: "",
                        order: "Asc",
                      });
                    },
                  },
                  {
                    title: "Alphabetical descending",
                    value: "Desc",
                    handleSelect: () => {
                      setSortType("Desc");
                      updateList({
                        search: "",
                        order: "Desc",
                      });
                    },
                  },
                ]}
                selectedSortType={sortType}
              />
              <IconButton
                data-automation="search-button"
                className={classes.searchIconButton}
                color="primary"
                onClick={() => setOpenSearch(true)}
              >
                <SearchRoundedIcon
                  color="primary"
                  className={classes.searchIcon}
                />
              </IconButton>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setAddOpenShopModal(true);
                }}
                style={{ paddingLeft: props.loading ? "12px" : "16px" }}
              >
                Add Vertical
              </Button>
            </div>
          </HeaderWithButtons.ButtonArea>
        </HeaderWithButtons>
        {/* CSS FIX : margin-left: auto */}

        {openSearch && (
          <Grid container className={classes.searchBarArea}>
            <Grid item xs={11}>
              <TextField
                style={{ width: "100%" }}
                variant="outlined"
                name="Search"
                placeholder="Search Vertical"
                label="Search"
                value={searchTerm}
                onChange={handleSearch}
              ></TextField>
            </Grid>
            <Grid item xs={1} className={classes.searchCancelButtonArea}>
              <Button
                id="basic-button"
                aria-controls="basic-menu"
                aria-haspopup="true"
                onClick={() => {
                  setSearchTerm("");
                  setOpenSearch(false);
                }}
                className={classes.modalImageText}
              >
                {/* <img src={printIcon} className={classes.buttonIcon} alt="printIcon" /> */}
                <Typography
                  variant="button"
                  component="span"
                  color="primary"
                  className={classes.printButtonText}
                  onClick={() => {
                    setSearchTerm("");
                    updateList({
                      search: "",
                      order: sortType,
                    });
                  }}
                >
                  Cancel
                </Typography>
              </Button>
            </Grid>
          </Grid>
        )}

        <ClusteredList
          data={verticalData ?? []}
          bottomTextCreator={bottomTextCreator}
          topTextCreator={topTextCreator}
          pathCreator={pathCreator}
          headlineTypeSingular="Vertical"
          headLineTypePlural="Verticals"
        />
      </Container>
      <GeneralModal
        title="Add Vertical"
        description="Start by giving vertical a name and write a short description. Once added you will be able to add categories from vertical details screen."
        visible={openAddShopModal}
        onClose={setAddOpenShopModal}
        onSubmit={handleSubmit}
        modalWidth="560px"
      >
        <GeneralModal.ContentArea>
          <Grid container>
            <Grid item xs={12}>
              <TextField
                placeholder="Vertical Name"
                value={newVerticalName}
                variant="outlined"
                margin="normal"
                fullWidth
                id="VerticalName"
                label="Vertical Name"
                name="VerticalName"
                onChange={(event: any) =>
                  setNewVerticalName(event.target.value)
                }
                error={nameError}
                InputProps={{
                  classes: {
                    root: classes.cssOutlinedInput,
                    focused: classes.cssFocused,
                    notchedOutline: classes.notchedOutline,
                  },
                }}
              />
              <TextField
                placeholder="Vertical Description"
                value={newVerticalDescription}
                onChange={(event: any) =>
                  setNewVerticalDescription(event.target.value)
                }
                variant="outlined"
                margin="normal"
                fullWidth
                id="VerticalDescription"
                label="Vertical Description"
                name="VerticalDescription"
                InputProps={{
                  classes: {
                    root: classes.cssOutlinedInput,
                    focused: classes.cssFocused,
                    notchedOutline: classes.notchedOutline,
                  },
                }}
              />
            </Grid>
          </Grid>
        </GeneralModal.ContentArea>
        <GeneralModal.ButtonArea>
          <Button
            onClick={() => {
              setAddOpenShopModal(false);
            }}
            className={classes.cancelButton}
          >
            <Typography
              onClick={() => {
                setNameError(false);
                setNewVerticalName("");
                setNewVerticalDescription("");
                setAddOpenShopModal(false);
              }}
              variant="button"
              component="p"
              color="primary"
            >
              Cancel
            </Typography>
          </Button>
          <Button onClick={handleSubmit} className={classes.submitButton}>
            <Typography variant="button" component="p" color="inherit">
              Submit
            </Typography>
          </Button>
        </GeneralModal.ButtonArea>
      </GeneralModal>
    </Box>
  );
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    justifyContent: "center",
  },
  searchIcon: {
    width: 24,
    height: 24,
  },
  searchIconButton: {
    width: 40,
    height: 40,
    marginLeft: 8,
  },
  root: {
    flexGrow: 1,
    maxWidth: "840px",
  },
  tabContainer: {
    backgroundColor: theme.palette.background.paper,
    paddingBottom: "8px",
  },
  mgLeftAuto: {
    display: "flex",
    flexDirection: "row",
  },
  submitButton: {
    width: "80px",
    height: "48px",
    borderRadius: "8px",
    padding: "6px 16px",
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },
  cancelButton: {
    width: "80px",
    height: "48px",
    borderRadius: "8px",
    padding: "6px 16px",
    backgroundColor: theme.palette.primary.light,
    marginRight: "8px",
  },
  cssOutlinedInput: {
    display: "flex",
    "&$cssFocused $notchedOutline": {
      border: `1px solid ${theme.palette.primary.main}`,

    },
  },

  cssFocused: {},

  notchedOutline: {
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  modalImageText: { display: "inline-block", verticalAlign: "middle" },
  modalImageTextArea: { display: "flex", alignItems: "center" },
  printButtonText: {
    textTransform: "none",
  },
  searchCancelButtonArea: {
    display: "flex",
    alignItems: "center",
  },
  searchBarArea: {
    marginTop: 25,
  },
});

export default Verticals;
